import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import LocationMap from '../components/LocationMap';
import SEO from '../components/SEO';
import {
  Star,
  Heart,
  Share2,
  X,
  ChevronLeft,
  ChevronRight,
  Medal,
  Shield,
  MessageSquare,
  Home as HomeIcon,
  BadgeCheck
} from 'lucide-react';
import { propertiesAPI, reviewsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import BookingWidget from '../components/BookingWidget';
import VerifiedHostBadge from '../components/VerifiedHostBadge';
import SustainabilityBadge from '../components/SustainabilityBadge';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get the correct image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=400';
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  if (imagePath.startsWith('/api/')) {
    return `${BACKEND_URL}${imagePath}`;
  }
  if (imagePath.startsWith('/uploads/')) {
    return `${BACKEND_URL}/api${imagePath}`;
  }
  return imagePath;
};

const PropertyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [property, setProperty] = useState(null);
  const [propertyReviews, setPropertyReviews] = useState([]);
  const [canReview, setCanReview] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({}); // { room_id: quantity }

  const updateCart = (roomId, qty) => {
    setCart((prev) => {
      const next = { ...prev };
      if (qty <= 0) delete next[roomId];
      else next[roomId] = qty;
      return next;
    });
  };

  const cartTotal = React.useMemo(() => {
    if (!property?.rooms) return { items: [], totalPerNight: 0, count: 0 };
    const items = Object.entries(cart).map(([rid, qty]) => {
      const room = property.rooms.find((r) => r.room_id === rid);
      return room ? { room, qty } : null;
    }).filter(Boolean);
    return {
      items,
      totalPerNight: items.reduce((sum, x) => sum + x.room.price * x.qty, 0),
      count: items.reduce((sum, x) => sum + x.qty, 0),
    };
  }, [cart, property]);

  const handleProceedToCheckout = () => {
    if (property?.host_verified === false) {
      toast.error('Booking is disabled — this host is completing KYC verification');
      return;
    }
    if (!cartTotal.count) {
      toast.error('Please add at least one room');
      return;
    }
    const selections = Object.entries(cart).map(([rid, qty]) => `${rid}:${qty}`).join(',');
    navigate(`/booking/${property._id || property.id}?cart=${encodeURIComponent(selections)}`);
  };
  const [isFavorite, setIsFavorite] = useState(false);
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Check wishlist status and review eligibility
  useEffect(() => {
    if (isAuthenticated && user?.user_id && id) {
      axios.get(`${BACKEND_URL}/api/wishlist/check/${user.user_id}/${id}`)
        .then(res => { if (res.data.is_wishlisted) setIsFavorite(true); })
        .catch(() => {});
    }
    if (isAuthenticated && user?.email && id) {
      axios.get(`${BACKEND_URL}/api/reviews/can-review/${id}/${user.email}`)
        .then(res => { if (res.data.can_review) setCanReview(true); })
        .catch(() => {});
    }
  }, [isAuthenticated, user, id]);

  const fetchPropertyData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch property details
      const propertyResponse = await propertiesAPI.getById(id);
      if (propertyResponse.success && propertyResponse.data) {
        setProperty(propertyResponse.data);
        
        const reviewsResponse = await reviewsAPI.getByPropertyId(id);
        if (reviewsResponse.success && reviewsResponse.data) {
          setPropertyReviews(reviewsResponse.data);
        }
      } else {
        toast.error('Property not found');
        navigate('/');
      }
    } catch (err) {
      console.error('Error fetching property:', err);
      toast.error('Failed to load property details');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchPropertyData();
  }, [fetchPropertyData]);

  const handleSubmitReview = async () => {
    if (!reviewForm.comment.trim()) {
      toast.error('Please write a comment');
      return;
    }
    setReviewSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/reviews/property/${id}`, {
        userName: user.name,
        userEmail: user.email,
        rating: reviewForm.rating,
        comment: reviewForm.comment
      });
      if (res.data.success) {
        toast.success('Review submitted!');
        setPropertyReviews(prev => [res.data.data, ...prev]);
        setShowReviewForm(false);
        setCanReview(false);
        setReviewForm({ rating: 5, comment: '' });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    }
    setReviewSubmitting(false);
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7CB342]"></div>
          <p className="mt-4 text-gray-600">Loading property...</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return <div className="flex items-center justify-center min-h-screen">Property not found</div>;
  }

  const handlePhotoNavigation = (direction) => {
    if (direction === 'next') {
      setCurrentPhotoIndex((prev) => (prev + 1) % property.images.length);
    } else {
      setCurrentPhotoIndex((prev) => (prev - 1 + property.images.length) % property.images.length);
    }
  };

  // Photo Gallery Modal
  if (showAllPhotos) {
    return (
      <div className="fixed inset-0 z-50 bg-black">
        <div className="relative h-full">
          <Button
            onClick={() => setShowAllPhotos(false)}
            variant="ghost"
            className="absolute top-4 left-4 z-10 text-white hover:bg-white/20 rounded-full"
          >
            <X className="h-6 w-6" />
          </Button>

          <div className="flex items-center justify-center h-full">
            <Button
              onClick={() => handlePhotoNavigation('prev')}
              variant="ghost"
              className="absolute left-4 text-white hover:bg-white/20 rounded-full p-3"
            >
              <ChevronLeft className="h-8 w-8" />
            </Button>

            <img
              src={getImageUrl(property.images[currentPhotoIndex])}
              alt={`View ${currentPhotoIndex + 1}`}
              className="max-h-full max-w-full object-contain"
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=400';
              }}
            />

            <Button
              onClick={() => handlePhotoNavigation('next')}
              variant="ghost"
              className="absolute right-4 text-white hover:bg-white/20 rounded-full p-3"
            >
              <ChevronRight className="h-8 w-8" />
            </Button>

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm">
              {currentPhotoIndex + 1} / {property.images.length}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO
        path={`/property/${property.slug || id}`}
        title={`${property.title} — ${property.location}`}
        description={`Book "${property.title}" in ${property.location}. ${property.bedrooms} BR · ${property.guests} guests · ₹${property.price?.toLocaleString?.() || property.price}/night. ${(property.description || '').slice(0, 120)}`}
        keywords={`${property.title}, ${property.location}, homestay in ${property.location}, villa in ${property.location}, holiday home ${property.location}, ${property.property_type || ''}, ${property.category || ''}`}
        image={property.images?.[0]}
        type="product"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'LodgingBusiness',
          name: property.title,
          description: property.description,
          image: property.images || [],
          address: {
            '@type': 'PostalAddress',
            addressLocality: property.location,
            addressCountry: 'IN',
          },
          priceRange: `INR ${property.price}`,
          starRating: property.rating ? {
            '@type': 'Rating',
            ratingValue: property.rating,
          } : undefined,
          aggregateRating: property.rating && property.reviews_count ? {
            '@type': 'AggregateRating',
            ratingValue: property.rating,
            reviewCount: property.reviews_count,
          } : undefined,
          amenityFeature: (property.amenities || []).map((a) => ({
            '@type': 'LocationFeatureSpecification',
            name: a,
          })),
          numberOfRooms: property.bedrooms,
          occupancy: {
            '@type': 'QuantitativeValue',
            maxValue: property.guests,
          },
        }}
      />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-2">
            {property.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center space-x-1">
              <Star className="h-4 w-4 fill-current text-gray-900" />
              <span className="font-medium">{property.rating}</span>
              <span className="text-gray-500">·</span>
              <button className="underline font-medium hover:text-gray-700">
                {property.reviews} reviews
              </button>
            </div>
            <span className="text-gray-500">·</span>
            <button className="underline font-medium hover:text-gray-700">
              {property.location}, {property.country}
            </button>
            <div className="ml-auto flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                className="rounded-lg"
                onClick={async () => {
                  const url = window.location.href;
                  if (navigator.share) {
                    try {
                      await navigator.share({ title: property.title, url });
                    } catch (err) {
                      if (err.name !== 'AbortError') {
                        await navigator.clipboard.writeText(url);
                        toast.success('Link copied to clipboard');
                      }
                    }
                  } else {
                    await navigator.clipboard.writeText(url);
                    toast.success('Link copied to clipboard');
                  }
                }}
                data-testid="share-btn"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="rounded-lg"
                onClick={async () => {
                  if (!isAuthenticated) {
                    toast.error('Please log in to save properties', {
                      action: { label: 'Log in', onClick: () => navigate('/login') }
                    });
                    return;
                  }
                  try {
                    const res = await axios.post(`${BACKEND_URL}/api/wishlist/toggle`, {
                      user_id: user.user_id,
                      property_id: id
                    });
                    if (res.data.action === 'added') {
                      setIsFavorite(true);
                      toast.success('Added to wishlist');
                    } else {
                      setIsFavorite(false);
                      toast.info('Removed from wishlist');
                    }
                  } catch (err) {
                    toast.error('Failed to update wishlist');
                  }
                }}
              >
                <Heart className={`h-4 w-4 mr-2 ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
                Save
              </Button>
            </div>
          </div>
        </div>

        {/* Photo Grid */}
        <div className="grid grid-cols-4 grid-rows-2 gap-2 rounded-xl overflow-hidden mb-12 h-[500px]">
          <div
            className="col-span-2 row-span-2 cursor-pointer group relative overflow-hidden"
            onClick={() => {
              setCurrentPhotoIndex(0);
              setShowAllPhotos(true);
            }}
          >
            <img
              src={getImageUrl(property.images[0])}
              alt="Main view"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=400';
              }}
            />
          </div>
          {property.images.slice(1, 5).map((image, index) => (
            <div
              key={`${image}-${index}`}
              className="cursor-pointer group relative overflow-hidden"
              onClick={() => {
                setCurrentPhotoIndex(index + 1);
                setShowAllPhotos(true);
              }}
            >
              <img
                src={getImageUrl(image)}
                alt={`View ${index + 2}`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=400';
                }}
              />
              {index === 3 && property.images.length > 5 && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="text-white font-semibold">+{property.images.length - 5} photos</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - Property Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Host Info */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-semibold mb-2">
                    {property.type} hosted by {property.host.name}
                  </h2>
                  {property.host_verified && (
                    <div className="mb-2">
                      <VerifiedHostBadge size="md" />
                    </div>
                  )}
                  {property.sustainability?.verified && property.sustainability?.verified_tier && (
                    <div className="mb-2">
                      <SustainabilityBadge tier={property.sustainability.verified_tier} score={property.sustainability.score} size="md" />
                    </div>
                  )}
                  <div className="flex items-center space-x-2 text-gray-600">
                    <span>{property.guests} guests</span>
                    <span>·</span>
                    <span>{property.bedrooms} bedrooms</span>
                    <span>·</span>
                    <span>{property.beds} beds</span>
                    <span>·</span>
                    <span>{property.bathrooms} bathrooms</span>
                  </div>
                </div>
                <Avatar className="h-14 w-14">
                  <AvatarImage src={property.host.image} />
                  <AvatarFallback>{property.host.name[0]}</AvatarFallback>
                </Avatar>
              </div>
              <Separator />
            </div>

            {/* Highlights */}
            <div className="space-y-6">
              {property.host.isSuperhost && (
                <div className="flex items-start space-x-4">
                  <Medal className="h-6 w-6 text-[#7CB342] mt-1" />
                  <div>
                    <h3 className="font-semibold">Superhost</h3>
                    <p className="text-gray-600 text-sm">
                      Superhosts are experienced, highly rated hosts who are committed to providing great stays.
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-start space-x-4">
                <HomeIcon className="h-6 w-6 text-gray-900 mt-1" />
                <div>
                  <h3 className="font-semibold" data-testid="listing-type-title">
                    {property.listing_type === 'rooms' ? 'Private rooms' : 'Entire home'}
                  </h3>
                  <p className="text-gray-600 text-sm" data-testid="listing-type-description">
                    {property.listing_type === 'rooms'
                      ? 'Book individual rooms in this property. Shared spaces may include kitchen, lounge, or garden.'
                      : "You'll have the entire place to yourself."}
                  </p>
                </div>
              </div>
              {property.instantBook && (
                <div className="flex items-start space-x-4">
                  <Shield className="h-6 w-6 text-gray-900 mt-1" />
                  <div>
                    <h3 className="font-semibold">Instant Book</h3>
                    <p className="text-gray-600 text-sm">
                      Book without waiting for host approval.
                    </p>
                  </div>
                </div>
              )}
            </div>
            <Separator />

            {/* Description */}
            <div>
              <p className="text-gray-700 leading-relaxed">{property.description}</p>
            </div>
            <Separator />

            {/* Rooms (for per-room listings) */}
            {property.listing_type === 'rooms' && property.rooms?.length > 0 && (
              <>
                <div data-testid="rooms-section">
                  <h2 className="text-2xl font-semibold mb-6">Rooms available</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {property.rooms.map((room) => {
                      const inCart = cart[room.room_id] || 0;
                      return (
                        <Card key={room.room_id} className="border-2 hover:border-[#7CB342] transition-all" data-testid={`room-card-${room.room_id}`}>
                          <CardContent className="p-5 space-y-3">
                            {room.photos?.[0] && (
                              <img src={room.photos[0]} alt={room.category} className="w-full h-32 object-cover rounded-md" />
                            )}
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-semibold text-lg text-gray-900">{room.category}</h3>
                                {room.description && (
                                  <p className="text-xs text-gray-500 mt-0.5">{room.description}</p>
                                )}
                              </div>
                              <Badge className="bg-[#7CB342]/10 text-[#7CB342] hover:bg-[#7CB342]/10">
                                {room.inventory} available
                              </Badge>
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                              <span>👥 {room.max_guests} guests</span>
                              <span>🛏️ {room.beds} {room.beds > 1 ? 'beds' : 'bed'}</span>
                              <span>🛁 {room.bathrooms} bath</span>
                            </div>
                            <div className="flex items-end justify-between pt-2 border-t">
                              <div>
                                <div className="text-2xl font-bold text-gray-900">₹{room.price.toLocaleString()}</div>
                                <div className="text-xs text-gray-500">per night</div>
                              </div>
                              <div className="flex items-center gap-2">
                                {inCart > 0 ? (
                                  <div className="flex items-center gap-1 bg-[#7CB342]/10 rounded-md px-2 py-1">
                                    <button onClick={() => updateCart(room.room_id, inCart - 1)} className="text-[#7CB342] hover:bg-[#7CB342]/20 rounded w-7 h-7 flex items-center justify-center font-bold" data-testid={`cart-dec-${room.room_id}`}>-</button>
                                    <span className="w-6 text-center font-semibold" data-testid={`cart-qty-${room.room_id}`}>{inCart}</span>
                                    <button onClick={() => updateCart(room.room_id, Math.min(room.inventory, inCart + 1))} disabled={inCart >= room.inventory} className="text-[#7CB342] hover:bg-[#7CB342]/20 rounded w-7 h-7 flex items-center justify-center font-bold disabled:opacity-40" data-testid={`cart-inc-${room.room_id}`}>+</button>
                                  </div>
                                ) : (
                                  <Button
                                    onClick={() => updateCart(room.room_id, 1)}
                                    className="bg-[#7CB342] hover:bg-[#6da036] text-white"
                                    data-testid={`add-to-cart-${room.room_id}-btn`}
                                  >
                                    Add to booking
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
                <Separator />
              </>
            )}

            {/* Amenities */}
            <div>
              <h2 className="text-2xl font-semibold mb-6">What this place offers</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {property.amenities.map((amenity) => (
                  <div key={amenity} className="flex items-center space-x-3 py-3">
                    <span className="text-xl">✓</span>
                    <span className="text-gray-700">{amenity}</span>
                  </div>
                ))}
              </div>
            </div>
            <Separator />

            {/* Reviews */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <Star className="h-6 w-6 fill-current text-gray-900" />
                  <h2 className="text-2xl font-semibold">
                    {property.rating} · {propertyReviews.length} review{propertyReviews.length !== 1 ? 's' : ''}
                  </h2>
                </div>
                {canReview && !showReviewForm && (
                  <Button
                    onClick={() => setShowReviewForm(true)}
                    className="bg-[#7CB342] hover:bg-[#6da036] text-white"
                    data-testid="write-review-btn"
                  >
                    Write a Review
                  </Button>
                )}
              </div>

              {/* Review Form */}
              {showReviewForm && (
                <Card className="mb-6 border-[#7CB342]/30">
                  <CardContent className="p-5 space-y-4">
                    <h3 className="font-semibold text-lg">Share your experience</h3>
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Rating</p>
                      <div className="flex space-x-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={() => setReviewForm(prev => ({ ...prev, rating: star }))}
                            className="p-0.5"
                            data-testid={`star-rating-${star}`}
                          >
                            <Star className={`h-6 w-6 ${star <= reviewForm.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <textarea
                      className="w-full border rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#7CB342]/50"
                      rows={4}
                      placeholder="Tell others about your stay..."
                      value={reviewForm.comment}
                      onChange={(e) => setReviewForm(prev => ({ ...prev, comment: e.target.value }))}
                      data-testid="review-comment-input"
                    />
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setShowReviewForm(false)}>Cancel</Button>
                      <Button
                        onClick={handleSubmitReview}
                        disabled={reviewSubmitting}
                        className="bg-[#7CB342] hover:bg-[#6da036] text-white"
                        data-testid="submit-review-btn"
                      >
                        {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {propertyReviews.length === 0 ? (
                <p className="text-gray-500 text-sm">No reviews yet. Be the first to review!</p>
              ) : (
                <div className="space-y-6">
                  {propertyReviews.map((review, idx) => (
                    <Card key={review.id || idx} className="border-0 shadow-none">
                      <CardContent className="p-0">
                        <div className="flex items-start space-x-4">
                          <Avatar>
                            <AvatarImage src={review.userImage} />
                            <AvatarFallback>{review.userName?.[0] || 'G'}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h4 className="font-semibold">{review.userName}</h4>
                              {review.verified_guest && (
                                <span className="inline-flex items-center text-xs text-[#7CB342] font-medium">
                                  <BadgeCheck className="h-3.5 w-3.5 mr-0.5" />
                                  Verified Guest
                                </span>
                              )}
                              <span className="text-gray-500 text-sm">{review.date}</span>
                            </div>
                            <div className="flex items-center space-x-1 mb-2">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-3 w-3 ${
                                    i < review.rating ? 'fill-current text-gray-900' : 'text-gray-300'
                                  }`}
                                />
                              ))}
                            </div>
                            <p className="text-gray-700 text-sm leading-relaxed">{review.comment}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Booking Widget / Cart Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              {property.listing_type === 'rooms' ? (
                cartTotal.count > 0 ? (
                  <Card className="border-2 border-[#7CB342]/40 shadow-lg" data-testid="cart-summary">
                    <CardContent className="p-5 space-y-3">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-gray-900">Your booking</h3>
                        <Badge className="bg-[#7CB342] text-white">{cartTotal.count} room{cartTotal.count > 1 ? 's' : ''}</Badge>
                      </div>
                      <div className="space-y-2">
                        {cartTotal.items.map(({ room, qty }) => (
                          <div key={room.room_id} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">{room.category}</div>
                              <div className="text-xs text-gray-500">{qty} × ₹{room.price.toLocaleString()}</div>
                            </div>
                            <button onClick={() => updateCart(room.room_id, 0)} className="text-xs text-red-500 hover:text-red-700 ml-2" data-testid={`cart-remove-${room.room_id}`}>Remove</button>
                          </div>
                        ))}
                      </div>
                      <div className="pt-2 border-t flex justify-between font-semibold text-gray-900">
                        <span>Per night</span>
                        <span>₹{cartTotal.totalPerNight.toLocaleString()}</span>
                      </div>
                      <p className="text-[11px] text-gray-400">+ 10% platform fee + GST (5% or 18%) at checkout</p>
                      {property.host_verified === false ? (
                        <>
                          <Button
                            className="w-full py-5 text-base cursor-not-allowed"
                            disabled
                            data-testid="proceed-checkout-btn"
                          >
                            Continue with {cartTotal.count} room{cartTotal.count > 1 ? 's' : ''}
                          </Button>
                          <p
                            className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2 text-center"
                            data-testid="host-kyc-notice"
                          >
                            <strong>Host KYC in progress.</strong> Booking opens once identity is verified.
                          </p>
                        </>
                      ) : (
                        <Button
                          onClick={handleProceedToCheckout}
                          className="w-full bg-[#7CB342] hover:bg-[#6da036] text-white py-5 text-base"
                          data-testid="proceed-checkout-btn"
                        >
                          Continue with {cartTotal.count} room{cartTotal.count > 1 ? 's' : ''}
                        </Button>
                      )}
                      <p className="text-xs text-center text-gray-400">You&apos;ll pick check-in/out dates next</p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="border-2 border-[#7CB342]/30 bg-[#7CB342]/5">
                    <CardContent className="p-6 text-center">
                      <p className="text-sm text-gray-700 mb-2">Multi-room property</p>
                      <p className="text-xs text-gray-500">Add rooms to your booking from the list to continue.</p>
                    </CardContent>
                  </Card>
                )
              ) : (
                <BookingWidget property={property} />
              )}
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="mt-12">
          <Separator className="mb-8" />
          <h2 className="text-2xl font-semibold mb-6">Where you'll be</h2>
          <p className="text-gray-700 mb-4">{property.location}, {property.country}</p>
          <LocationMap location={property.location} country={property.country} />
        </div>

        {/* Host Info Card */}
        <div className="mt-12">
          <Separator className="mb-8" />
          <div className="flex items-start space-x-6">
            <Avatar className="h-16 w-16">
              <AvatarImage src={property.host.image} />
              <AvatarFallback>{property.host.name[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h2 className="text-2xl font-semibold mb-2">Hosted by {property.host.name}</h2>
              <p className="text-gray-600 mb-4">Joined in {property.host.joinedYear}</p>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {property.host_verified && <VerifiedHostBadge size="lg" />}
                {property.host.isSuperhost && (
                  <Badge className="bg-[#7CB342] hover:bg-[#6da036]">
                    <Medal className="h-3 w-3 mr-1" />
                    Superhost
                  </Badge>
                )}
              </div>
              <Button className="mt-4 rounded-lg">
                <MessageSquare className="h-4 w-4 mr-2" />
                Contact host
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetailPage;